/**
 * The Quotation Print Package: an optional cover letter, and Business Terms that
 * are NOT the same thing.
 *
 * THE RECOVERED 1.6.2 BEHAVIOUR — preserved exactly
 * ------------------------------------------------
 * Inspecting the frozen 1.6.2 app showed a real feature, not an imagined one:
 *
 *   - The cover letter is OPTIONAL ("Add a covering letter if required",
 *     user-guide.html:562).
 *   - It is PLAIN TEXT, HTML-escaped and rendered with `white-space: pre-wrap`
 *     (quotation-preview.html:181-187, 608-611). NOT markup.
 *   - It prints as PAGE 1, with the company letterhead.
 *   - The QUOTATION is page 2 onward, and the letterhead REPEATS there.
 *   - The company signature and "Authorised Signatory" sit on PAGE 2, with the
 *     quotation — NOT on the cover-letter page.
 *   - There is NO separate print action. It is one quotation print package.
 *
 * TWO 1.6.2 DEFECTS THAT MUST NOT BE INHERITED
 * -------------------------------------------
 * 1. **The page break was UNCONDITIONAL.** `quotation-preview.html:1293` always
 *    emitted `.q-page-break` and the PDF always called `newPage()`, so a
 *    quotation with NO cover letter still printed as two pages — the second one
 *    preceded by a near-empty one. Here the break exists ONLY when the letter is
 *    enabled. `enabled: false` produces no page at all, not a blank one.
 *
 * 2. **"No cover letter" was NOT EXPRESSIBLE.** `quotation.coveringLetter ||
 *    buildDefaultCoveringLetter()` (js/quotation.js:472) regenerated the default
 *    whenever the stored value was empty, so a user who deliberately cleared the
 *    letter got it back on reopening the quotation. Here `enabled` is an explicit
 *    boolean. An empty string is CONTENT, not absence.
 *
 * WHY THERE IS NO `signature` FIELD — LOCKED, DELIBERATELY ABSENT
 * ---------------------------------------------------------------
 * A quotation does not have its own signature image. It uses the signature
 * configured in Business Profile, and that is the ONLY signature source.
 *
 * A per-quotation `signature` field was considered and rejected. Two competing
 * signature sources is a question the UI would have to ask on every quotation,
 * one of the two would go stale, and which of them a signed quotation actually
 * used would depend on a field-presence check rather than a deliberate choice.
 * Inventing per-quotation signer behaviour is out of scope.
 *
 * So the signature is not a property of the LETTER either — it belongs to the
 * QUOTATION page and comes from the business. See `QuotationPage.signature`.
 *
 * COVER LETTER AND BUSINESS TERMS ARE SEPARATE
 * -------------------------------------------
 * 1.6.2 had two distinct textareas: `quotCoveringLetter` and `quotBusinessTerms`.
 * They stay different things — the letter is a customer-facing introduction, the
 * terms are commercial conditions. Merging them would make "leave the letter out
 * but keep the payment terms" inexpressible, and that is an ordinary thing to want.
 */
import type { ComputedDocument } from '@core/calc';

/**
 * The optional cover letter.
 *
 * NO `signature` field — see the note above. Its presence here would be a
 * regression, not an oversight.
 */
export interface QuotationCoverLetter {
  /**
   * Explicit on/off.
   *
   * Not derived from `content` being empty. That inference IS the 1.6.2 defect: it
   * makes "I chose no letter" and "I wrote an empty letter" indistinguishable, and
   * the workaround then re-introduces a default.
   */
  readonly enabled: boolean;
  /** PLAIN TEXT, with newlines preserved. Never HTML — 1.6.2 escaped it. */
  readonly content: string;
  /** When the user last changed it. Recorded, not enforced. */
  readonly updatedAt?: string;
}

/**
 * Business Terms & Conditions: commercial conditions, separate from the letter.
 *
 * Rendered on the QUOTATION page, not the cover-letter page — the terms are
 * something the customer agrees to, and 1.6.2 put them with the quotation.
 */
export interface QuotationBusinessTerms {
  readonly content: string;
}

/** Where the company signature comes from. Business Profile, and only there. */
export interface QuotationSignature {
  readonly companyName: string;
  /** Data URL or asset path of the configured signature image, if any. */
  readonly imageUrl?: string;
  /** The closing label. 1.6.2 used "Authorised Signatory". */
  readonly label: string;
}

export interface QuotationLetterhead {
  readonly companyName: string;
  readonly addressLines: readonly string[];
  readonly logoUrl?: string;
  readonly gstin?: string;
}

/** The cover-letter page. Page 1, and ONLY when the letter is enabled. */
export interface CoverLetterPage {
  readonly kind: 'cover-letter';
  /** The company letterhead. Repeats on the quotation pages too. */
  readonly letterhead: QuotationLetterhead;
  readonly letter: QuotationCoverLetter;
  /**
   * No signature on this page — and that is a TYPE, not a convention.
   *
   * 1.6.2's signature belongs to the quotation. Making it structurally impossible
   * to put a signature on this page is the only way to be sure a later renderer
   * does not "helpfully" add one.
   */
  readonly signature: never;
}

/** The quotation itself: letterhead repeated, signature on it. */
export interface QuotationPage {
  readonly kind: 'quotation';
  readonly letterhead: QuotationLetterhead;
  /** EVERY amount here comes from `computeDocument`. Nothing is recomputed. */
  readonly totals: ComputedDocument;
  readonly terms?: QuotationBusinessTerms;
  /** From Business Profile. Present on this page only, per 1.6.2. */
  readonly signature: QuotationSignature;
}

/** The whole package: cover letter (maybe) then the quotation (always). */
export type QuotationPageEntry = CoverLetterPage | QuotationPage;

export interface QuotationPrintPackage {
  readonly pages: readonly QuotationPageEntry[];
  /** True when a cover-letter page will be produced. Drives the UI's badge. */
  readonly hasCoverLetter: boolean;
}


/**
 * Assemble the ordered pages of a Quotation Print Package.
 *
 * The ORDER is the contract: the cover letter, when present, is first and the
 * quotation always follows. Both pages carry the letterhead, because 1.6.2
 * repeated it and a quotation is a multi-page document a reader must be able to
 * attribute to a company on every sheet.
 *
 * A DISABLED letter yields a ONE-PAGE package. Not a two-page package with a
 * blank first page, and not a package with an empty `content` — the page is not
 * produced at all, which is the only way "no cover letter" can be both honest
 * and printable.
 */
export function buildQuotationPrintPackage(input: {
  readonly letterhead: QuotationLetterhead;
  readonly signature: QuotationSignature;
  readonly totals: ComputedDocument;
  readonly coverLetter?: QuotationCoverLetter;
  readonly businessTerms?: QuotationBusinessTerms;
}): QuotationPrintPackage {
  const hasCoverLetter = input.coverLetter?.enabled === true;
  const pages: QuotationPageEntry[] = [];

  if (hasCoverLetter && input.coverLetter) {
    pages.push({
      kind: 'cover-letter',
      letterhead: input.letterhead,
      // `content` is passed through UNTOUCHED — no trimming, no escaping, no
      // defaulting. An empty-but-ENABLED letter still prints a page: the user
      // asked for one, and dropping it would be the 1.6.2 defect from the
      // opposite direction.
      letter: input.coverLetter,
      // `signature: never` — see CoverLetterPage. Written explicitly so the
      // absence is a decision recorded here rather than a property merely omitted.
      signature: undefined as never,
    });
  }

  pages.push({
    kind: 'quotation',
    letterhead: input.letterhead,
    totals: input.totals,
    // Terms ride with the quotation, per 1.6.2, and are dropped entirely when
    // blank so no empty box is printed.
    ...(input.businessTerms && input.businessTerms.content.trim()
      ? { terms: input.businessTerms }
      : {}),
    signature: input.signature,
  });

  return { pages, hasCoverLetter };
}

/**
 * Whether this package prints on more than one page.
 *
 * Exposed because "a quotation with no cover letter is ONE page" is a locked
 * decision, and a property a test can assert is worth more than a comment nobody
 * re-reads before adding an unconditional `newPage()`.
 */
export function isSinglePage(pkg: QuotationPrintPackage): boolean {
  return pkg.pages.length === 1 && pkg.pages[0]?.kind === 'quotation';
}

